import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowDownCircle, ArrowUpCircle, ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useUpcoming } from "@/hooks/useAccounting";
import { mergeUpcoming, type UpcomingItem } from "@/lib/accounting/accountingView";
import { formatAccountingAmount } from "@/lib/accounting/format";
import { cn } from "@/lib/utils";

/**
 * Échéances à venir : timeline fusionnée des sorties dues (dépenses) et des
 * entrées attendues (échéancier client), groupée par mois (repliable), avec
 * solde projeté.
 */
interface Props {
  period: { start_date: string; end_date: string };
}

export function UpcomingTab({ period }: Props) {
  const { t, i18n } = useTranslation(["accounting"]);
  const { currency } = useCurrency();
  const fmt = (v: number) => formatAccountingAmount(v, currency);
  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

  const { data, isLoading } = useUpcoming(period);
  const items = useMemo(() => mergeUpcoming(data ?? null, 0), [data]);
  const totals = useMemo(() => {
    let inflow = 0, outflow = 0;
    for (const i of items) (i.direction === "in" ? (inflow += i.amount) : (outflow += i.amount));
    return { inflow, outflow, net: inflow - outflow };
  }, [items]);

  // Regroupement par mois (yyyy-MM) : un bloc repliable par mois.
  const groups = useMemo(() => {
    const map = new Map<string, UpcomingItem[]>();
    for (const item of items) {
      const key = item.date.slice(0, 7);
      const list = map.get(key);
      if (list) list.push(item);
      else map.set(key, [item]);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, rows]) => ({
        month,
        rows,
        inflow: rows.reduce((s, r) => s + (r.direction === "in" ? r.amount : 0), 0),
        outflow: rows.reduce((s, r) => s + (r.direction === "out" ? r.amount : 0), 0),
      }));
  }, [items]);

  // Tous les mois ouverts par défaut ; l'utilisateur peut replier.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  useEffect(() => { setCollapsed({}); }, [period.start_date, period.end_date]);

  const monthLabel = (month: string) => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: "long", year: "numeric" });
  };

  // Arriéré : dépenses dues AVANT aujourd'hui — hors timeline projetée.
  const overdue = data?.expenses_overdue ?? [];
  const overdueTotal = useMemo(
    () => overdue.reduce((s, e) => s + (Number(e.amount) || 0), 0),
    [overdue]
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: t("upcoming.expected"), value: totals.inflow, accent: "text-emerald-600" },
          { label: t("upcoming.due"), value: totals.outflow, accent: "text-red-600" },
          { label: t("upcoming.net"), value: totals.net, accent: totals.net >= 0 ? "text-emerald-600" : "text-red-600" },
        ].map((k) => (
          <div key={k.label} className="rounded-xl border bg-card px-3 py-2">
            <div className="text-[11px] md:text-xs text-muted-foreground leading-tight">{k.label}</div>
            <div className={cn("text-sm md:text-lg font-semibold tabular-nums", k.accent)}>{fmt(k.value)}</div>
          </div>
        ))}
      </div>

      {/* Section arriéré (dépenses en retard) */}
      {!isLoading && overdue.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-400">
              <AlertTriangle className="h-4 w-4" /> {t("upcoming.overdue", { count: overdue.length })}
            </div>
            <b className="text-sm text-red-700 dark:text-red-400 tabular-nums">{fmt(overdueTotal)}</b>
          </div>
          <div className="space-y-1.5">
            {overdue.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-lg bg-background/80 border px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {e.suppliers?.name ? `${e.label} — ${e.suppliers.name}` : e.label}
                  </div>
                  <div className="text-xs text-muted-foreground">{e.due_date ?? e.expense_date}</div>
                </div>
                <div className="text-sm font-semibold tabular-nums text-red-600 shrink-0">−{fmt(Number(e.amount) || 0)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : groups.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t("upcoming.empty")}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const isCollapsed = !!collapsed[group.month];
            return (
              <div key={group.month} className="rounded-xl border bg-card overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [group.month]: !c[group.month] }))}
                  aria-expanded={!isCollapsed}
                  className="w-full flex flex-col gap-1 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors sm:flex-row sm:items-center sm:gap-3"
                >
                  <span className="flex min-w-0 items-center gap-2 sm:flex-1">
                    <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform", isCollapsed && "-rotate-90")} />
                    <span className="text-sm font-semibold capitalize truncate">{monthLabel(group.month)}</span>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">{group.rows.length}</span>
                  </span>
                  <span className="flex items-center gap-3 pl-6 sm:pl-0">
                    <span className="text-sm tabular-nums text-emerald-600">+{fmt(group.inflow)}</span>
                    <span className="text-sm tabular-nums text-red-600">−{fmt(group.outflow)}</span>
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="divide-y border-t">
                    {group.rows.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 px-3 py-2.5">
                        {item.direction === "in" ? (
                          <ArrowUpCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                        ) : (
                          <ArrowDownCircle className="h-5 w-5 shrink-0 text-red-500" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">
                            {item.direction === "in" ? t("upcoming.installment", { label: item.label }) : item.label}
                          </div>
                          <div className="text-xs text-muted-foreground">{item.date}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={cn("text-sm font-semibold tabular-nums", item.direction === "in" ? "text-emerald-600" : "text-red-600")}>
                            {item.direction === "in" ? "+" : "−"}{fmt(item.amount)}
                          </div>
                          <div className="text-[10px] sm:text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
                            <span className="hidden sm:inline">{t("upcoming.projected")} : </span>
                            {fmt(item.projected)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
